import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Filter } from "lucide-react";
import { useAnonymousData } from "../../StudentData/hooks/useAnonymousData";
import { StudentSupportContext } from "../../../context/studentSupport.context";
import { Info, User } from "lucide-react";
import NoData from "../components/NoData";
import FilterRowStudentsSupports from "./components/FilterRowStudentsSupports";
import FiltersPanel from "./components/FiltersPanel";
import StudentList from "./components/StudentList";
import StudentDetailModal from "./components/StudentDetailModal";
import { handleTime } from "../../StudentData/handlers/timeHandler";
import { useAlerts } from "@/hooks/useAlerts";
import { usePinnedStudents } from "@/hooks/usePinnedStudents";
import { findStudentClass } from "@/screens/Dashboards/utils/studentSupportsUtils";
import { handleStudent } from "@/screens/StudentData/handlers/studentHandler";
import { handleMultiEmotion } from "@/screens/StudentData/handlers/multiEmotionHandler";
import { Link, useLocation } from "react-router-dom";
import { useDeepCompareMemo } from "use-deep-compare";
import isEqual from "lodash.isequal";
import { useDebouncedCallback } from "use-debounce";
import { useOnboardingUiEnabled } from "@/hooks/useOnboardingUiEnabled";
import { useIsMobileViewport } from "@/hooks/useIsMobileViewport";
import { useBootstrap } from "@/features/bootstrap";
import SharingOffSafetyAlerts from "./components/SharingOffSafetyAlerts";

function StudentSupports(props) {
  const { data: user } = useBootstrap();
  const location = useLocation();
  const [initialSearchTerm, setInitialSearchTerm] = useState("");
  const [autoSelectStudentEmail, setAutoSelectStudentEmail] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedStudentData, setSelectedStudentData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const prevTimeRef = useRef(null);
  const listRef = useRef(null);
  const [filteredStudentsCount, setFilteredStudentsCount] = useState(0);

  const contextData = useContext(StudentSupportContext);
  const anonymousData = useAnonymousData(user, props.anonamizeData);

  const { state, updateState } = props.anonamizeData
    ? anonymousData
    : contextData;

  const debouncedUpdate = useDebouncedCallback((newState) => {
    updateState(newState);
  }, 200);

  const { alerts } = useAlerts({
    timeRange: state.chosenTime,
  });

  const { pinnedStudents, togglePinnedStudent } = usePinnedStudents();

  const handleFilteredStudentsChange = useCallback((count) => {
    setFilteredStudentsCount(count);
  }, []);

  const isTourActive = useOnboardingUiEnabled();
  const isMobileViewport = useIsMobileViewport();
  const demoImgSrc = (src) =>
    isMobileViewport ? src.replace(/-demo(\.[a-z]+)$/i, "-mobile$1") : src;

  const showTourDemoWidget =
    isTourActive &&
    state.wellnessLoadedPeriods?.["2months"] === true &&
    (state.currentStudents.length === 0 ||
      (state.actionsCounter <= 0 &&
        (state.chosenStudent === "All Students" ||
          state.chosenStudent === "Choose Student")));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const studentEmail = params.get("studentEmail");
    const studentEmailHash = params.get("studentEmailHash");

    let resolvedEmail = studentEmail;
    // Resolve hashed email using client-known map and shared salt
    if (!resolvedEmail && studentEmailHash) {
      const salt = "DapKdh";
      try {
        const candidates = Object.keys(state?.studentEmailToNameMap || {});
        const encoder = new TextEncoder();
        const importKey = (rawKey) =>
          window.crypto.subtle.importKey(
            "raw",
            encoder.encode(rawKey),
            { name: "HMAC", hash: { name: "SHA-256" } },
            false,
            ["sign"],
          );
        const toHex = (buf) =>
          Array.from(new Uint8Array(buf))
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");
        (async () => {
          const key = await importKey(salt);
          for (const candidate of candidates) {
            const sig = await window.crypto.subtle.sign(
              "HMAC",
              key,
              encoder.encode(candidate),
            );
            if (toHex(sig) === studentEmailHash) {
              resolvedEmail = candidate;
              break;
            }
          }
          if (resolvedEmail) {
            setAutoSelectStudentEmail(resolvedEmail);
            const nameCandidate =
              state?.studentEmailToNameMap?.[resolvedEmail] || resolvedEmail;
            setInitialSearchTerm(nameCandidate);
            handleStudentClick(resolvedEmail);
          }
        })();
      } catch (e) {
        // ignore
      }
    } else if (
      resolvedEmail &&
      // Only open students on this user's own roster. Previously any address
      // in ?studentEmail= opened the detail view and requested that student's
      // wellness data. The server now refuses out-of-scope students too; this
      // keeps the page from trying.
      state?.studentEmailToNameMap?.[resolvedEmail]
    ) {
      setAutoSelectStudentEmail(resolvedEmail);
      setInitialSearchTerm(state.studentEmailToNameMap[resolvedEmail]);
      handleStudentClick(resolvedEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, state?.studentEmailToNameMap]);

  useEffect(() => {
    if (
      state.chosenTime &&
      state.chosenTime !== "" &&
      state.chosenTime !== prevTimeRef.current
    ) {
      prevTimeRef.current = state.chosenTime;
      const newState = handleTime(state, state.chosenTime, true);
      if (!isEqual(newState, state)) {
        debouncedUpdate(newState);
      }
    }
  }, [state, debouncedUpdate]);

  const allEmotions = state.multiEmotion.emotions || [];
  const formattedEmotions =
    allEmotions.length === 0
      ? ""
      : allEmotions.length === 9 &&
          allEmotions.every((emotion) =>
            [
              "sad",
              "tired",
              "lonely",
              "happy",
              "grateful",
              "calm",
              "stressed",
              "scared",
              "angry",
            ].includes(emotion),
          )
        ? "All Emotions"
        : allEmotions.length === 5 &&
            allEmotions.every((emotion) =>
              ["sad", "lonely", "stressed", "angry", "scared"].includes(
                emotion,
              ),
            )
          ? "Challenging Emotions"
          : allEmotions.length === 1
            ? allEmotions[0]
            : allEmotions.slice(0, -1).join(", ") +
              " & " +
              allEmotions[allEmotions.length - 1];

  const scopeInfo = useDeepCompareMemo(() => {
    const chosenStudent = state?.chosenStudent;
    const isSpecificStudent =
      chosenStudent && chosenStudent !== "" && chosenStudent !== "All Students";
    if (isSpecificStudent) return null;

    const chosenDistrict = state?.chosenDistrict || "All Districts";
    const chosenSchool = state?.chosenSchool || "All Schools";
    const chosenClass = state?.chosenClass || "All Classes";

    if (chosenClass && chosenClass !== "All Classes") {
      return { prefix: "from", label: `${chosenClass}` };
    }

    if (chosenSchool && chosenSchool !== "All Schools") {
      return { prefix: "from", label: chosenSchool };
    }

    if (chosenDistrict && chosenDistrict !== "All Districts") {
      return { prefix: "from", label: chosenDistrict };
    }

    if (chosenSchool) return { prefix: "from", label: chosenSchool };
    if (chosenDistrict) return { prefix: "from", label: chosenDistrict };
    return { prefix: "from", label: "All Students" };
  }, [
    state?.chosenDistrict,
    state?.chosenSchool,
    state?.chosenClass,
    state?.chosenStudent,
  ]);

  const filterCount = useDeepCompareMemo(() => {
    let count = 0;
    if (state.chosenTime && state.chosenTime !== "") {
      count++;
    }
    if (
      state.chosenPercentage &&
      state.chosenPercentage > 0 &&
      state.chosenPercentage !== ""
    ) {
      count++;
    }

    if (formattedEmotions && formattedEmotions.length > 0) {
      count++;
    }

    if (state.chosenAlertType && state.chosenAlertType !== "") {
      count++;
    }

    if (
      state.chosenAlertStatus &&
      state.chosenAlertStatus !== "" &&
      state.chosenAlertStatus !== "all"
    ) {
      count++;
    }

    return count;
  }, [
    state.chosenTime,
    state.chosenPercentage,
    formattedEmotions,
    state.chosenAlertType,
    state.chosenAlertStatus,
  ]);

  const handleStudentClick = useCallback(
    async (email) => {
      setSelectedStudent(email);
      setIsModalOpen(true);

      const found = state?.districts
        ? await findStudentClass(state.districts, email)
        : null;
      const district =
        found?.district || state.chosenDistrict || "All Districts";
      const school = found?.school || state.chosenSchool || "All Schools";
      const className = found?.className || state.chosenClass || "All Classes";

      let studentState = {
        ...state,
        chosenDistrict: district,
        chosenSchool: school,
        chosenClass: className,
        chosenStudent: email,
        currentStudents: [email],
      };

      studentState = {
        ...studentState,
        ...handleStudent(studentState, email),
      };

      studentState = {
        ...studentState,
        ...handleMultiEmotion(studentState, state.multiEmotion.emotions),
      };

      setSelectedStudentData(studentState);

      if (
        state.chosenStudent !== "All Students" &&
        !isEqual(state, studentState)
      ) {
        updateState(studentState);
      }
    },
    [state, updateState],
  );

  function removeSelectedStudent() {
    setSelectedStudent(null);
    setSelectedStudentData(null);
  }

  useEffect(() => {
    if (!isModalOpen) {
      removeSelectedStudent();
    }
  }, [
    state.chosenDistrict,
    state.chosenSchool,
    state.chosenClass,
    state.chosenStudent,
    isModalOpen,
  ]);

  const handleCloseModal = () => {
    setSelectedStudent(null);
    setSelectedStudentData(null);
    setIsModalOpen(false);
  };

  const filteredAlerts = useDeepCompareMemo(() => {
    if (!state.chosenStudent || !alerts || !selectedStudentData) return [];

    const studentGroup = alerts.find(
      (group) =>
        group.student?.email === (selectedStudentData || state).chosenStudent,
    );

    if (!studentGroup) return [];

    return (studentGroup.alerts || [])
      .filter((alert) => {
        const typeMatch =
          !state.chosenAlertType ||
          state.chosenAlertType === "all" ||
          alert.type.kind === state.chosenAlertType;

        const statusMatch =
          !state.chosenAlertStatus ||
          state.chosenAlertStatus === "all" ||
          (state.chosenAlertStatus === "seen" && alert.read) ||
          (state.chosenAlertStatus === "unseen" && !alert.read);

        return typeMatch && statusMatch;
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [alerts, state, selectedStudentData]);

  return (
    <div className="sm:ml-[130px] border border-[#DDDDDD] bg-[#F4F4F3] flex flex-col min-h-screen"
      // data-onboarding-id="student-supports-top"
    >
      {!isModalOpen && (
        <div className="flex justify-between items-center space-x-4 mt-14 bg-[#F4F4F3] md:mt-0 px-4 py-3 sm:px-8 sm:py-5 sticky top-0 z-10 border-b-2">
          <span className="text-[24px] text-500 font-medium capitalize ">
            Student Supports: MTSS
          </span>
          <div className="flex flex-col sm:hidden">
            <button
              className="flex items-center gap-2 p-2 text-base text-400 border border-[#DDDDDD] rounded"
              onClick={() => setFiltersOpen(true)}
              data-onboarding-id="student-supports-filters"
            >
              <span>
                <Filter className="w-4 h-4 text-gray-600" />
              </span>
              Filters
              <span className="rounded-full bg-[#1879EE] text-white text-[11px] px-2 font-semibold">
                {filterCount > 0 && <span className="">{filterCount}</span>}
              </span>
            </button>
          </div>
        </div>
      )}
      <div className="px-3 pt-2 sm:px-8 pb-10 sm:pt-4 flex flex-col gap-3">
        {!isModalOpen && (
          <div className="flex flex-col gap-1 px-1 py-2">
            <div className="flex flex-row gap-2 items-center">
              <div className="group cursor-pointer">
                <Info className="w-4 h-4 text-[#1879EE]" />
                <p className="absolute w-[75%] sm:w-2/3 top-30 left-30 mt-2 p-3 bg-white shadow text-[14px] rounded hidden group-hover:block z-30">
                  The 1-2-3 Wellness "MTSS" can serve as helpful, proactive
                  educational tool when educators include it as part of a larger
                  framework when considering students who may benefit from
                  additional support (tier 2: yellow, tier 3: red). MTSS
                  "rules" can be set in four ways: Default rules (set by 1-2-3 Wellness), District-wide rules (set by
              by superadmins), School-wide rules (set by school admins), and Custom rules (set by each individual educator).
                  Please note that while this data can help educators better
                  understand student wellness, we strongly encourage educators
                  to also speak with students, school mental health
                  professionals and actively seek out other data as needed when
                  determining how to best support student needs. Please do not
                  use 1-2-3 Wellness data as the sole criterion when considering
                  which students would benefit from tier 2 or tier 3 supports.
                  We encourage schools to research their own tier 2 and 3
                  decision making rules and change their 1-2-3 Wellness MTSS
                  settings to best meet the school/district needs.
                </p>
              </div>
              <h3 className="flex-1 text-[16px]">
                Discover students who may benefit from{" "}
                <span className="text-[#E09C5C] font-medium">tier 2</span> and{" "}
                <span className="text-[#C53030] font-medium">tier 3</span>{" "}
                supports. Click a student’s name to help you develop
                personalized wellness solutions. Change your tier 2/3 settings{" "}
                <Link
                  to="/optionsscreen"
                  className="underline font-medium"
                  data-onboarding-id="student-supports-tier-settings"
                >
                  here
                </Link>
                .
              </h3>
            </div>
          </div>
        )}
        <div className="flex lg:flex-row lg:gap-3" data-onboarding-id="student-supports-top">
          <div
            className={`${
              isModalOpen ? "hidden lg:flex lg:w-1/3 px-4" : "w-full"
            } flex flex-col gap-3 max-h-screen`}
          >
            <SharingOffSafetyAlerts
              alertGroups={alerts}
              highlightAlertId={new URLSearchParams(location.search).get(
                "alertId",
              )}
            />
            <FilterRowStudentsSupports
              state={state}
              userRole={user?.role}
              updateState={updateState}
              filterCount={filterCount}
              onStudentClick={handleStudentClick}
              removeSelectedStudent={removeSelectedStudent}
              initialSearchTerm={initialSearchTerm}
              autoSelectStudentEmail={autoSelectStudentEmail}
              filteredStudentsCount={filteredStudentsCount}
              isModalOpen={isModalOpen}
              user={user}
            />
            {filteredStudentsCount > 0 && (
              <div className="flex flex-row items-center gap-2 bg-[#1879EE17] text-[#005BE2] p-3 rounded my-2 sm:mt-3 sm:mb-3">
                <User className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                <span className="text-sm text-400">
                  <strong>Showing</strong>: {filteredStudentsCount}{" "}
                  {filteredStudentsCount === 1 ? "student" : "students"}{" "}
                  {scopeInfo?.label ? (
                    <>
                      {scopeInfo.prefix}{" "}
                      <span className="font-semibold">
                        {scopeInfo.label}
                      </span>{" "}
                    </>
                  ) : null}
                  who felt <span>{formattedEmotions} </span>
                  {state.chosenPercentage > 0 &&
                    state.chosenPercentage !== "" && (
                      <span>over {state.chosenPercentage}% of time </span>
                    )}
                  in the last <span>{state.chosenTime}</span>
                  {filterCount > 0 && (
                    <span className="ml-2">
                      {filterCount <= 3
                        ? `(${filterCount} filter${filterCount > 1 ? "s" : ""})`
                        : `(+${filterCount - 3} more filter${
                            filterCount - 3 > 1 ? "s" : ""
                          } )`}
                    </span>
                  )}
                </span>
              </div>
            )}
            <div
              ref={listRef}
              className="flex-1 min-h-0 overflow-y-auto"
              style={{ position: "relative" }}
              data-onboarding-id="student-supports-student-names"
            >
              {state.wellnessLoadedPeriods?.["2months"] === true && (
                <NoData
                  hasStudents={state.currentStudents.length > 0}
                  hasActivity={state.actionsCounter > 0}
                  chosenStudent={state.chosenStudent}
                  onboardingNoDataId="student-supports"
                />
              )}
              {showTourDemoWidget ? (
                <div className="bg-white shadow-sm rounded p-3 border border-black/5">
                  <div className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-900 ring-1 ring-amber-200">
                    Example with demo data
                  </div>
                  <img
                    src={demoImgSrc(
                      "/images/onboardingDemo/studentSupports-demo.png",
                    )}
                    alt="Student Supports list example (demo only)"
                    className="mt-2 w-full object-contain rounded-xl border border-black/10 bg-white"
                    loading="lazy"
                    draggable={false}
                  />
                </div>
              ) : (
                <StudentList
                  state={state}
                  updateState={updateState}
                  onStudentClick={handleStudentClick}
                  selectedStudent={selectedStudent}
                  formattedEmotions={formattedEmotions}
                  allEmotions={state.multiEmotion.emotions || []}
                  filterCount={filterCount}
                  alerts={alerts}
                  pinnedStudents={pinnedStudents}
                  userId={user?._id}
                  togglePinnedStudent={togglePinnedStudent}
                  isModal={false}
                  isModalOpen={isModalOpen}
                  onFilteredStudentsChange={handleFilteredStudentsChange}
                  listRef={listRef}
                />
              )}
            </div>
          </div>
          {isModalOpen && (
            <StudentDetailModal
              selectedStudent={selectedStudent}
              state={state}
              studentData={selectedStudentData || state}
              updateState={updateState}
              onClose={handleCloseModal}
              formattedEmotions={formattedEmotions}
              allEmotions={state.multiEmotion.emotions || []}
              userId={user?._id}
              user={user}
              filterCount={filterCount}
              alerts={alerts}
              pinnedStudents={pinnedStudents}
              togglePinnedStudent={togglePinnedStudent}
              filteredAlerts={filteredAlerts}
            />
          )}
        </div>
      </div>
      <FiltersPanel
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        state={state}
        updateState={updateState}
        filteredStudentsCount={filteredStudentsCount}
      />
    </div>
  );
}

export default React.memo(StudentSupports, (prevProps, nextProps) =>
  isEqual(prevProps, nextProps),
);
